/**
 * @fileoverview A single "My tasks" card. Mirrors the prototype layout (Mantine
 * v6) and dispatches by `task.type`. Defensive: missing labels render as "—".
 */

import { ComponentType } from 'react'
import { Badge, Box, Button, Card, Center, Divider, Flex, Group, Stack, Text } from '@mantine/core'
import { IconClipboardCheck, IconClockExclamation, IconUpload } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { CARD_ACTION_WIDTH } from '../constants'
import type { StudentTask, StudentTaskType } from '../types'
import { EMPTY_VALUE, formatDate, lessonLabel } from '../utils'

interface StudentTaskCardProps {
  task: StudentTask
  onOpen: (task: StudentTask) => void
}

interface TypeConfig {
  typeLabel: string
  action: string
  doneBadge: string
  icon: ComponentType<{ size?: number }>
  twoColumns: boolean
  showDue: boolean
}

const CONFIG: Record<StudentTaskType, TypeConfig> = {
  deliverable: {
    typeLabel: 'deliverable',
    action: 'submit_deliverable',
    doneBadge: 'submitted',
    icon: IconUpload,
    twoColumns: true,
    showDue: true,
  },
  'self-assessment': {
    typeLabel: 'self_assessment',
    action: 'self_assess',
    doneBadge: 'completed',
    icon: IconClipboardCheck,
    twoColumns: true,
    showDue: false,
  },
  'final-self-assessment': {
    typeLabel: 'final_self_assessment',
    action: 'submit_final_assessment',
    doneBadge: 'completed',
    icon: IconClipboardCheck,
    twoColumns: false,
    showDue: false,
  },
}

const Column = ({ label, value }: { label: string; value: string }) => (
  <Stack spacing={0} style={{ flex: 1, minWidth: 0 }}>
    <Text size="sm" color="dimmed">{label}</Text>
    <Text size="sm" weight={500}>{value}</Text>
  </Stack>
)

export function StudentTaskCard({ task, onOpen }: StudentTaskCardProps) {
  const { t } = useTranslation()
  const tr = (key: string) => t(`student_tasks.cards.${key}`)
  const config = CONFIG[task.type]
  const ActionIcon = config.icon
  const showLate = task.overdue && !task.done

  return (
    <Card withBorder radius="md" padding="md">
      <Stack spacing="md">
        <Group position="apart" align="center" noWrap spacing="xs">
          <Group spacing="xs" noWrap>
            <Text weight={600} size="sm">{tr(config.typeLabel)}</Text>
            {task.done && (
              <Badge color="green" size="sm" variant="light">{tr(config.doneBadge)}</Badge>
            )}
            {!task.done && task.inProgress && (
              <Badge color="blue" size="sm" variant="light">{tr('in_progress')}</Badge>
            )}
          </Group>
          {config.showDue && task.deadline && (
            <Group spacing="xs" align="center" noWrap>
              <Text size="sm" color="dimmed">{tr('due_date')}</Text>
              <Text size="sm" weight={500}>{formatDate(task.deadline)}</Text>
              {showLate && (
                <Badge
                  color="red"
                  size="sm"
                  variant="light"
                  leftSection={
                    <Center inline>
                      <IconClockExclamation size={12} />
                    </Center>
                  }
                  styles={{ leftSection: { display: 'flex', alignItems: 'center' } }}
                >
                  {tr('late')}
                </Badge>
              )}
            </Group>
          )}
        </Group>

        <Divider />

        <Flex justify="space-between" align="center" gap="md" wrap="wrap">
          <Flex gap="md" style={{ flex: 1, minWidth: 0 }}>
            <Column label={tr('project')} value={task.projectName ?? EMPTY_VALUE} />
            {config.twoColumns && (
              <Column label={tr('lesson')} value={lessonLabel(task.lessonDate, task.lessonTitle)} />
            )}
          </Flex>
          <Box style={{ flexShrink: 0, width: CARD_ACTION_WIDTH, maxWidth: '100%' }}>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ActionIcon size={16} />}
              onClick={() => onOpen(task)}
              fullWidth
            >
              {tr(config.action)}
            </Button>
          </Box>
        </Flex>
      </Stack>
    </Card>
  )
}
